import React, { useMemo, useState } from 'react';
import { Button, CreateButton, ModalDocument, TableDocument } from '@/components';
import { Image } from 'antd';
import { Flex, Container } from '@/ui';
import { Title } from '@/ui/typography';
import { Text } from '@/ui/typography';
import {
  ENABLED_DOC_TO_VIEW_STATUSES_ADMIN,
  ENABLED_STATUSES_INVESTOR,
  STATUS
} from '@/utils/static/kyc';
import { getUrlName } from '@/utils/helpers';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { adminKYCApi } from '@/api';
import noDocumentsImg from '@/assets/user/no-documents.png';
import { useNavigate } from 'react-router';
import { usePopups } from '@/hooks';

/**
 * @param documents (array of requested docs)
 * @param onDelete (func) - handle delete doc
 */
const enabledStatuses = {
  investor: ENABLED_STATUSES_INVESTOR,
  referral: ENABLED_STATUSES_INVESTOR,
  admin: ENABLED_DOC_TO_VIEW_STATUSES_ADMIN
};
export const ProfileDocuments = ({
  documents,
  questsDocs,
  refetchDocs,
  viewMode = 'investor',
  investorId,
  referralId
}) => {
  const { t } = useTranslation();
  const { showErrorToast } = usePopups();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const investor = useSelector((state) => state.investor);
  const [addDocVisible, setAddDocVisible] = useState(false);

  const getUserRole = () => {
    if (investor.isAgent) return 'AGENT';
    if (investor.isRecommender) return 'RECOMMENDER';
    return investor.account === 'COMPANY' ? 'COMPANY' : 'PERSONAL';
  };

  //array of sections
  const docsSections = useMemo(() => {
    try {
      if (documents) {
        const result = {};
        const userRole = getUserRole();

        documents.forEach((doc) => {
          const sectionDocs = result[doc.kycTypeDocument.kycDocumentSectionId];
          const docStatus = doc.status || STATUS.NEW;
          const rolesWithAccess = doc.kycTypeDocument?.rolesWithAccess;
          const hasAccess =
            viewMode === 'admin' ||
            !rolesWithAccess ||
            rolesWithAccess.length === 0 ||
            rolesWithAccess.includes(userRole);

          const formattedDoc = {
            ...doc,
            enabled:
              hasAccess &&
              (viewMode !== 'investor' || investor.accountStatus === 'ACTIVE') &&
              enabledStatuses[viewMode][docStatus]
          };

          if (!hasAccess) return;

          sectionDocs
            ? result[doc.kycTypeDocument.kycDocumentSectionId].docs.push(formattedDoc)
            : (result[doc.kycTypeDocument.kycDocumentSectionId] = {
                name: doc.kycDocumentSection.name,
                docs: [formattedDoc]
              });
        });

        return Object.keys(result).map((key) => result[key]);
      }

      return null;
    } catch (e) {}
  }, [documents, investor, viewMode]);

  const questsDocsFormatted = useMemo(() => {
    if (questsDocs) {
      return [
        {
          docs: questsDocs.map((doc, index) => {
            const { status, created, updated, expiration, person } =
              doc.investorQuestionSectionTypes;
            const { name } = doc.questionSectionType;
            const questionName = doc.question?.question;

            return {
              ...doc,
              status,
              created,
              updated,
              expiration,
              person,
              name: name ? name : getUrlName(doc.documentURL, index + 1),
              description: questionName,
              enabled: true
            };
          }),
          name: 'Documents from QUESTIONNAIRES'
        }
      ];
    }

    return null;
  }, [questsDocs]);

  const onOpenDocument = (id) => {
    switch (viewMode) {
      case 'investor': {
        navigate(`/documents/${id}`);
        break;
      }
      case 'referral': {
        navigate(`/referral/${referralId}/documents/${id}`);
        break;
      }
      case 'admin': {
        navigate(`/investor/${investor.id}/documents/${id}`);
        break;
      }
    }
  };

  const onDeleteDocument = async (requestDocumentId) => {
    try {
      const result = await adminKYCApi.deleteDocument(investorId, requestDocumentId);

      if (result) {
        await refetchDocs();
      }
    } catch (e) {
      showErrorToast(e, true);
    }
  };

  return (
    <Container $paddingY={25} $marginBottom={40}>
      <Flex align={'center'} justify={'space-between'} wrap>
        <Title level={2}>{t('Documents')}</Title>

        {!!docsSections && (
          <>
            <Container $smdHidden>
              <Container $width={180}>
                <Button
                  type='filled'
                  text={t('Add document')}
                  onClick={() => setIsModalOpen(true)}
                  disabled={
                    investor.accountStatus !== 'ACTIVE' ||
                    (investor.isAccessibleWithoutVerification ? false : !investor.isUserVerified)
                  }
                />
              </Container>
            </Container>

            <Container $hidden $smdVisible>
              <CreateButton onClick={() => setIsModalOpen(true)} />
            </Container>
          </>
        )}
      </Flex>

      {docsSections && docsSections.length > 0 ? (
        <Container $marginTop={40}>
          <TableDocument
            data={docsSections}
            onClick={onOpenDocument}
            adminView={viewMode === 'admin'}
            onDelete={onDeleteDocument}
            investorId={investor.id}
          />
        </Container>
      ) : (
        <Flex $marginTop={171} vertical align='center'>
          <Image src={noDocumentsImg} width={80} height={80} preview={false} />

          <Container $marginTop={40}>
            <Text $fontWeight={600} $fontSize={20}>
              No documents
            </Text>
          </Container>

          <Container $marginTop={12}>
            <Text>Add a document to make it appear</Text>
          </Container>

          {viewMode !== 'referral' && (
            <Container $marginTop={32} $width={170}>
              <Button
                type='filled'
                text='Add document'
                onClick={() => setIsModalOpen(true)}
                disabled={
                  viewMode === 'admin'
                    ? false
                    : investor.accountStatus !== 'ACTIVE' ||
                      (investor.isAccessibleWithoutVerification ? false : !investor.isUserVerified)
                }
              />
            </Container>
          )}
        </Flex>
      )}

      {questsDocsFormatted && questsDocsFormatted?.docs?.length > 0 && (
        <Container $marginTop={40}>
          <Container $marginBottom={20}>
            <Text>Documents from QUESTIONNAIRES</Text>
          </Container>
          <TableDocument
            data={questsDocsFormatted}
            onClick={onOpenDocument}
            adminView={viewMode === 'admin'}
            onDelete={onDeleteDocument}
            investorId={investor.id}
            isDocumentsFromQuestionnaires={true}
          />
        </Container>
      )}

      <ModalDocument
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        visible={addDocVisible}
        onClose={() => setAddDocVisible(false)}
        onSuccess={refetchDocs}
        adminView={viewMode === 'admin'}
        investorId={investor.id}
      />
    </Container>
  );
};
